import React, { ChangeEvent } from "react";

import { IconPosition } from "@at/design-system-elements";
import { IconInformation } from "@at/design-system-icons";

import { dataAttrTestId } from "@at/formatter-utils/lib/data-attr-utils/data-attr-utils";
import { snakeAndSentenceCaseToLowerCaseKebabCase } from "@at/formatter-utils/lib/string-utils/string.utils";
import { sortLeaseOffersByPriceAndDelivery } from "@at/leasing-utils";

import { InStockSelection } from "./stock-toggle.styled";
import {
  setLeasingModal,
  setSelectedLeaseOffer,
  setSelectedStockFilter,
} from "../../../../../contexts/leasing/actions/leasing.actions";
import {
  useLeasingDispatchContext,
  useLeasingStateContext,
} from "../../../../../contexts/leasing/context/leasing.context";
import {
  LeasingModals,
  ProductPageStockFilters,
} from "../../../../../contexts/leasing/types/leasing.types";

import { getHasInStockOffers } from "../../../../../utils/leasing/getHasInStockOffers/get-has-in-stock-offers";
import { getIsOfferInStock } from "../../../../../utils/leasing/getIsOfferInStock/get-is-offer-in-stock";
import { useTracking } from "../../../../../utils/tracking/use-tracking";
import * as Styled from "../../leasing-contract-customiser.styled";

export const inStockOptionLabel = "In stock options only";

export const StockToggle = () => {
  const state = useLeasingStateContext();
  const dispatch = useLeasingDispatchContext();
  const { selectedStockFilter, leaseContractType } = useLeasingStateContext();
  const { trackEvent } = useTracking();

  const { leaseOffersData, selectedLeaseOffer } = state;

  const handleStockToggle = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedStockFilter = event.target.checked
      ? ProductPageStockFilters.IN_STOCK
      : ProductPageStockFilters.ALL;

    const sortedLeaseOffers = sortLeaseOffersByPriceAndDelivery(
      leaseOffersData || [],
      leaseContractType,
    );
    if (
      selectedStockFilter === ProductPageStockFilters.IN_STOCK &&
      !getIsOfferInStock(selectedLeaseOffer?.deliveryEstimate)
    ) {
      const filteredAndSortedLeaseOffers = sortedLeaseOffers?.filter((offer) =>
        getIsOfferInStock(offer.deliveryEstimate),
      );

      const updatedLeaseOffer =
        filteredAndSortedLeaseOffers?.find(
          (offer) => offer.colour === selectedLeaseOffer?.colour,
        ) || filteredAndSortedLeaseOffers[0];

      dispatch(setSelectedLeaseOffer(updatedLeaseOffer));
    }

    dispatch(setSelectedStockFilter(selectedStockFilter));
    const trackingLabel = `terms-stock-${snakeAndSentenceCaseToLowerCaseKebabCase(
      selectedStockFilter,
    )}`;
    trackEvent({ trackingLabel });
  };

  const handleOpenAllOptionsInStockInfoModal = () => {
    dispatch(
      setLeasingModal({
        show: true,
        type: LeasingModals.ALL_OPTIONS_IN_STOCK,
        title: "All options In stock",
      }),
    );
  };

  const hasInStockOffersAvailable = getHasInStockOffers(leaseOffersData);

  const isInStockToggleDisabled = !hasInStockOffersAvailable;
  const isStockToggleChecked =
    selectedStockFilter === ProductPageStockFilters.IN_STOCK &&
    !isInStockToggleDisabled;

  return (
    <Styled.LeasingProductToggleArea
      {...{ [dataAttrTestId]: "leasing-stock-toggle" }}
    >
      <InStockSelection
        label={inStockOptionLabel}
        name="leasing-product-stock-toggle"
        id="leasing-product-filter-toggle"
        onChange={handleStockToggle}
        fieldType="switch"
        disabled={isInStockToggleDisabled}
        attributes={{ checked: isStockToggleChecked }}
      />
      <Styled.LeasingProductToggleInfo
        iconSrc={
          <IconInformation title="More information about availability" />
        }
        iconPosition={IconPosition.LEFT}
        displayType="tertiary"
        onInteraction={handleOpenAllOptionsInStockInfoModal}
      />
    </Styled.LeasingProductToggleArea>
  );
};
